import { useEffect, useMemo } from 'react';
import moment from 'moment';
import { CalendarClock, ChevronLeft, ChevronRight, MoreVertical } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { namespace } from '@common/config/constants';

import { useAppDispatch, useAppSelector } from '@common/reducers/hooks/store.hook';
import { getCalendarLeanEvents } from '@common/reducers/WhatsAppSelectors';
import { setCurrentDate } from '@common/reducers/CalendarReducer';
import { editCalendarEventThunk } from '@common/reducers/CalendarThunks';
import { getPendingUserMessages } from '@common/thunks/UserMessagesThunks';
import NewMessageFab from '@common/components/new-message-fab/new-message-fab';
import { groupEventsByDay } from './agenda-grouping';

/**
 * Dedicated MOBILE Planner — a first-class mobile presentation, NOT a squeezed
 * desktop calendar. It reuses the existing calendar LOGIC (the getCalendarLeanEvents
 * selector, the editCalendarEventThunk edit flow, and setCurrentDate navigation)
 * and forks only the RENDERING: the same events shown as day-grouped cards. A tap
 * on a card (or its ⋮) opens the existing scheduled-message editor via
 * editCalendarEventThunk — same action the desktop Agenda uses. Rendered at the
 * calendar-view container level (gated by useIsMobile), so it gets the full
 * viewport width instead of inheriting the desktop DialogContent chain.
 */
interface LeanEvent {
  _id: string;
  isCampaign: boolean;
  dueDate: string | Date;
  isRecurring?: boolean;
  message?: string;
  messageColor?: string;
  isPoll?: boolean;
  poll?: { question?: string } | null;
  contactList?: Array<{ id?: string; name?: string }> | null;
  [k: string]: any;
}

type Translate = (key: string, fallback: string) => string;

function dayLabel(date: Date, t: Translate): string {
  const today = moment().startOf('day');
  const d = moment(date).startOf('day');
  const diff = d.diff(today, 'days');
  if (diff === 0) return t('calendar.today', 'Today');
  if (diff === 1) return t('calendar.tomorrow', 'Tomorrow');
  if (diff === -1) return t('calendar.yesterday', 'Yesterday');
  return d.format('ddd, MMM D');
}

function eventTitle(e: LeanEvent): string {
  if (e.isCampaign) return 'Campaign';
  const c = e.contactList?.[0];
  if (c?.name) return c.name;
  if (c?.id) return c.id.split('@')[0];
  return 'Scheduled message';
}

function eventSnippet(e: LeanEvent): string {
  if (e.isPoll && e.poll?.question) return e.poll.question;
  return e.message || '';
}

export default function MobileAgenda() {
  const dispatch = useAppDispatch();
  const events = useAppSelector(getCalendarLeanEvents) as LeanEvent[];
  const currentDate = useAppSelector((s: any) => s.CalendarReducer.currentDate) as Date;

  // Load the pending scheduled messages on mount. The desktop CalendarView does
  // this too (calendar-view.tsx), but the mobile container renders MobileAgenda
  // INSTEAD of CalendarView — so without this the mobile agenda never fetches and
  // always shows "No scheduled messages" even when the account has some.
  const { t } = useTranslation(namespace);

  useEffect(() => {
    dispatch(getPendingUserMessages());
  }, [dispatch]);

  const groups = useMemo(() => groupEventsByDay(events || []), [events]);

  const openEvent = (e: LeanEvent) => {
    dispatch(editCalendarEventThunk({
      _id: e._id,
      isCampaign: e.isCampaign,
      dueDate: (e.dueDate ?? '').toString(),
      isRecurring: e.isRecurring || false,
    }));
  };

  return (
    <div style={{ height: '100%', width: '100%', minWidth: 0, maxWidth: '100%', overflowX: 'hidden', display: 'flex', flexDirection: 'column', background: 'var(--calendar-bg, #fff)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', borderBottom: '1px solid var(--color-border, #e5e7eb)', flexShrink: 0 }}>
        <button type="button" aria-label={t('calendar.previous', 'Previous')} onClick={() => dispatch(setCurrentDate(moment(currentDate).subtract(1, 'month').toDate()))} style={btnIcon}><ChevronLeft size={18} /></button>
        <button type="button" aria-label={t('calendar.next', 'Next')} onClick={() => dispatch(setCurrentDate(moment(currentDate).add(1, 'month').toDate()))} style={btnIcon}><ChevronRight size={18} /></button>
        <div style={{ fontSize: 15, fontWeight: 600, flex: 1, minWidth: 0, color: 'var(--color-text, #232323)' }}>{moment(currentDate).format('MMMM YYYY')}</div>
        <button type="button" onClick={() => dispatch(setCurrentDate(new Date()))} style={{ ...btnIcon, width: 'auto', minWidth: 64, padding: '0 14px', fontSize: 14, fontWeight: 600, color: 'var(--bt-blue, #2f80ed)' }}>{t('calendar.today')}</button>
      </div>

      <div style={{ flex: 1, minWidth: 0, overflowY: 'auto', overflowX: 'hidden', padding: '10px 12px 90px' }}>
        {groups.length === 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', padding: '64px 24px', gap: 14 }}>
            <div style={{
              width: 72, height: 72, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: 'var(--color-surface-accent, #eef2ff)', color: 'var(--bt-blue, #2f80ed)',
            }}>
              <CalendarClock size={32} />
            </div>
            <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--color-text, #232323)' }}>
              {t('calendar.noMessagesInRange', 'No scheduled messages this month')}
            </div>
            <div style={{ fontSize: 13.5, lineHeight: 1.5, color: 'var(--color-text-muted, #64748b)', maxWidth: 260 }}>
              {t('calendar.noMessagesHint', 'Tap + to schedule your first message.')}
            </div>
          </div>
        ) : (
          groups.map((g) => (
            <div key={g.dateKey} style={{ marginBottom: 4, minWidth: 0 }}>
              <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.4px', textTransform: 'uppercase', color: 'var(--color-text-muted, #64748b)', margin: '10px 2px 8px' }}>
                {dayLabel(g.date, (key, fallback) => t(key, fallback))} · {moment(g.date).format('MMM D')}
              </div>
              {g.items.map((e) => {
                const color = e.messageColor || (e.isCampaign ? 'var(--calendar-color-sage, #33B679)' : 'var(--calendar-color-peacock, #039BE5)');
                return (
                  <div
                    key={e._id}
                    role="button"
                    tabIndex={0}
                    onClick={() => openEvent(e)}
                    onKeyDown={(ev) => { if (ev.key === 'Enter' || ev.key === ' ') { ev.preventDefault(); openEvent(e); } }}
                    style={{
                      display: 'flex', gap: 11, alignItems: 'flex-start', position: 'relative',
                      width: '100%', maxWidth: '100%', minWidth: 0, boxSizing: 'border-box',
                      background: 'var(--color-surface, #fff)', border: '1px solid var(--color-border, #e5e7eb)',
                      borderLeft: `4px solid ${color}`, borderRadius: 14, padding: '12px 40px 12px 14px',
                      marginBottom: 10, cursor: 'pointer', boxShadow: '0 1px 2px rgba(16,24,40,.04)',
                    }}
                  >
                    <div style={{ flex: '0 0 auto', minWidth: 46, textAlign: 'center' }}>
                      <div style={{ fontSize: 15, fontWeight: 700, lineHeight: 1, color: 'var(--color-text, #232323)' }}>{moment(e.dueDate).format('HH:mm')}</div>
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13.5, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', color: 'var(--color-text, #232323)' }}>{eventTitle(e)}</div>
                      <div style={{ fontSize: 12.5, color: 'var(--color-text-muted, #64748b)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', marginTop: 2 }}>{eventSnippet(e)}</div>
                      <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
                        {e.isCampaign && <span style={pill('rgba(51,182,121,.14)', '#1a7f43')}>{t('calendar.campaign', 'Campaign')}</span>}
                        {e.isRecurring && <span style={pill('rgba(2,132,199,.12)', '#075985')}>⟳ {t('calendar.recurring', 'Recurring')}</span>}
                      </div>
                    </div>
                    <div aria-label="Event actions" onClick={(ev) => { ev.stopPropagation(); openEvent(e); }}
                      style={{ position: 'absolute', top: 10, insetInlineEnd: 8, width: 40, height: 40, borderRadius: 10, background: 'var(--color-surface-muted, #eef1f4)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8' }}>
                      <MoreVertical size={16} />
                    </div>
                  </div>
                );
              })}
            </div>
          ))
        )}
      </div>

      {/* Compose entry. The desktop CalendarView renders this FAB too; since the
          mobile container swaps in MobileAgenda, without it there is no way to
          create a scheduled message on mobile. The editor popup it opens is
          rendered at the calendar-view container level (works on mobile). */}
      <NewMessageFab />
    </div>
  );
}

// 44x44 is the accessible minimum tap target; these were 32x32, which is hard to
// hit reliably on a phone — the main reason month navigation felt fiddly on mobile.
const btnIcon: React.CSSProperties = {
  width: 44, height: 44, borderRadius: 12, border: '1px solid var(--color-border, #e5e7eb)',
  background: 'var(--color-surface, #fff)', color: '#334155', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0,
};
function pill(bg: string, color: string): React.CSSProperties {
  return { fontSize: 10.5, fontWeight: 600, borderRadius: 999, padding: '3px 9px', background: bg, color };
}
